"use client";

import { c as _c } from "react/compiler-runtime";
import React, { createContext, useContext, useEffect, useReducer } from "react";
import { defaultPlaygroundState, defaultSessionConfig } from "@/data/playground-state";
import { defaultPresets } from "@/data/presets";
import { playgroundStateHelpers } from "@/lib/playground-state-helpers";
import type { PlaygroundState } from "@/data/playground-state";
import type { Preset } from "@/data/presets";
import type { Dispatch, ReactNode } from "react";
const LS_OPENAI_API_KEY_NAME = "OPENAI_API_KEY";
const LS_USER_PRESETS_KEY = "PG_USER_PRESETS";
const LS_SELECTED_PRESET_ID_KEY = "PG_SELECTED_PRESET_ID";
const presetStorageHelper = {
  getStoredPresets: (): Preset[] => {
    const storedPresets = localStorage.getItem(LS_USER_PRESETS_KEY);
    return storedPresets ? JSON.parse(storedPresets) : [];
  },
  setStoredPresets: (presets: Preset[]): void => {
    localStorage.setItem(LS_USER_PRESETS_KEY, JSON.stringify(presets));
  },
  getStoredSelectedPresetId: (): string => {
    return localStorage.getItem(LS_SELECTED_PRESET_ID_KEY) || defaultPresets[0].id;
  },
  setStoredSelectedPresetId: (presetId: string | null): void => {
    if (presetId !== null) {
      localStorage.setItem(LS_SELECTED_PRESET_ID_KEY, presetId);
    } else {
      localStorage.removeItem(LS_SELECTED_PRESET_ID_KEY);
    }
  }
};

// Define action types and payloads
type Action = {
  type: "SET_SESSION_CONFIG";
  payload: Partial<PlaygroundState["sessionConfig"]>;
} | {
  type: "SET_API_KEY";
  payload: string | null;
} | {
  type: "SET_INSTRUCTIONS";
  payload: string;
} | {
  type: "SET_USER_PRESETS";
  payload: Preset[];
} | {
  type: "SET_SELECTED_PRESET_ID";
  payload: string | null;
} | {
  type: "SAVE_USER_PRESET";
  payload: Preset;
} | {
  type: "DELETE_USER_PRESET";
  payload: string;
};

// Create the reducer function

function playgroundStateReducer(state: PlaygroundState, action: Action): PlaygroundState {
  switch (action.type) {
    case "SET_SESSION_CONFIG":
      return {
        ...state,
        sessionConfig: {
          ...state.sessionConfig,
          ...action.payload
        }
      };
    case "SET_API_KEY":
      if (action.payload) {
        localStorage.setItem(LS_OPENAI_API_KEY_NAME, action.payload);
      } else {
        localStorage.removeItem(LS_OPENAI_API_KEY_NAME);
      }
      return {
        ...state,
        openaiAPIKey: action.payload
      };
    case "SET_INSTRUCTIONS":
      return {
        ...state,
        instructions: action.payload
      };
    case "SET_USER_PRESETS":
      return {
        ...state,
        userPresets: action.payload
      };
    case "SET_SELECTED_PRESET_ID":
      {
        presetStorageHelper.setStoredSelectedPresetId(action.payload);
        const newState = {
          ...state,
          selectedPresetId: action.payload
        };
        newState.instructions = playgroundStateHelpers.getSelectedPreset(newState)?.instructions || "";
        newState.sessionConfig = playgroundStateHelpers.getSelectedPreset(newState)?.sessionConfig || defaultSessionConfig;
        return newState;
      }
    case "SAVE_USER_PRESET":
      {
        const updatedPresetsAdd = state.userPresets.map(preset => preset.id === action.payload.id ? action.payload : preset);
        if (!updatedPresetsAdd.some(preset => preset.id === action.payload.id)) {
          updatedPresetsAdd.push(action.payload);
        }
        presetStorageHelper.setStoredPresets(updatedPresetsAdd);
        return {
          ...state,
          userPresets: updatedPresetsAdd
        };
      }
    case "DELETE_USER_PRESET":
      {
        const updatedPresetsDelete = state.userPresets.filter((preset: Preset) => preset.id !== action.payload);
        presetStorageHelper.setStoredPresets(updatedPresetsDelete);
        return {
          ...state,
          userPresets: updatedPresetsDelete
        };
      }
    default:
      return state;
  }
}

// Updated context type without auth dialog properties
interface PlaygroundStateContextProps {
  pgState: PlaygroundState;
  dispatch: Dispatch<Action>;
  helpers: typeof playgroundStateHelpers;
}
const PlaygroundStateContext = createContext<PlaygroundStateContextProps | undefined>(undefined);

// Create a custom hook to use the global state
export const usePlaygroundState = () => {
  const context = useContext(PlaygroundStateContext);
  if (!context) {
    throw new Error("usePlaygroundState must be used within a PlaygroundStateProvider");
  }
  return context;
};

// Create the provider component
interface PlaygroundStateProviderProps {
  children: ReactNode;
}
export const PlaygroundStateProvider = t0 => {
  const $ = _c(7);
  const {
    children
  } = t0;
  const [state, dispatch] = useReducer(playgroundStateReducer, defaultPlaygroundState);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const envApiKey = process.env.NEXT_PUBLIC_OPENAI_API_KEY;
      if (envApiKey && envApiKey.length >= 1) {
        dispatch({
          type: "SET_API_KEY",
          payload: envApiKey
        });
      } else {
        const storedKey = localStorage.getItem(LS_OPENAI_API_KEY_NAME);
        if (storedKey && storedKey.length >= 1) {
          dispatch({
            type: "SET_API_KEY",
            payload: storedKey
          });
        } else {
          dispatch({
            type: "SET_API_KEY",
            payload: null
          });
          console.warn("No OpenAI API key found in environment variables or localStorage");
        }
      }
      const storedPresets = localStorage.getItem(LS_USER_PRESETS_KEY);
      const userPresets = storedPresets ? JSON.parse(storedPresets) : [];
      dispatch({
        type: "SET_USER_PRESETS",
        payload: userPresets
      });
      const urlData = playgroundStateHelpers.decodeFromURLParams(window.location.search);
      if (urlData.state.selectedPresetId) {
        const defaultPreset = playgroundStateHelpers.getDefaultPresets().find(preset => preset.id === urlData.state.selectedPresetId);
        if (defaultPreset) {
          dispatch({
            type: "SET_SELECTED_PRESET_ID",
            payload: defaultPreset.id
          });
          return;
        }
        if (urlData.preset && urlData.preset.name) {
          const newPreset = {
            id: urlData.state.selectedPresetId,
            name: urlData.preset.name || "Shared Preset",
            description: urlData.preset.description,
            instructions: urlData.state.instructions || "",
            sessionConfig: urlData.state.sessionConfig || defaultSessionConfig,
            defaultGroup: undefined
          };
          const updatedUserPresets = [...userPresets, newPreset];
          presetStorageHelper.setStoredPresets(updatedUserPresets);
          dispatch({
            type: "SET_USER_PRESETS",
            payload: updatedUserPresets
          });
          dispatch({
            type: "SET_SELECTED_PRESET_ID",
            payload: newPreset.id
          });
        }
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== state) {
    t3 = {
      pgState: state,
      dispatch,
      helpers: playgroundStateHelpers
    };
    $[2] = state;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== children || $[5] !== t3) {
    t4 = <PlaygroundStateContext.Provider value={t3}>{children}</PlaygroundStateContext.Provider>;
    $[4] = children;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};